import { Input, Form, Space, Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useState, useCallback, useRef } from 'react';

interface MenuSearchFormProps {
  type: 'device' | 'inventory' | 'task';
  placeholder: string;
  fieldName?: string;
  resolveDeviceByInventoryNumber?: (inventoryNumber: string) => Promise<number>;
}

function inventoryLookupErrorKey(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'reason' in error) {
    if (error.reason === 'not_found') return 'menu:devices_search_by_inventory_not_found';
    if (error.reason === 'ambiguous') return 'menu:devices_search_by_inventory_ambiguous';
  }
  return 'menu:devices_search_by_inventory_error';
}

export function MenuSearchForm({
  type,
  placeholder,
  fieldName = 'id',
  resolveDeviceByInventoryNumber,
}: MenuSearchFormProps) {
  const { t } = useTranslation(['common', 'menu']);
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const resolvingRef = useRef(false);
  const [resolveError, setResolveError] = useState('');

  const submit = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed || resolvingRef.current) {
      return;
    }

    const deviceBasePath = '/equipment/devices';
    const warehouseInventoryPath = '/warehouse/inventory';

    switch (type) {
      case 'device':
        if (fieldName === 'inventory_number') {
          if (!resolveDeviceByInventoryNumber) {
            setResolveError(t('menu:devices_search_by_inventory_error'));
            return;
          }
          setResolveError('');
          resolvingRef.current = true;
          setIsResolving(true);
          try {
            const deviceId = await resolveDeviceByInventoryNumber(trimmed);
            navigate(`${deviceBasePath}/${deviceId}`);
          } catch (error) {
            setResolveError(t(inventoryLookupErrorKey(error)));
            return;
          } finally {
            resolvingRef.current = false;
            setIsResolving(false);
          }
        } else {
          navigate(`${deviceBasePath}/${trimmed}`);
        }
        break;
      case 'inventory':
        {
          const search = createSearchParams({ id: trimmed }).toString();
          navigate(`${warehouseInventoryPath}?${search}`);
        }
        break;
      case 'task':
        navigate(`/tasks/${trimmed}`);
        break;
    }
    
    setValue('');
  }, [fieldName, navigate, resolveDeviceByInventoryNumber, t, type, value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit();
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void submit();
  };

  return (
    <Form
      onSubmitCapture={handleSubmit}
      onClick={(event) => event.stopPropagation()}
      style={{ padding: '4px 16px' }}
    >
      <Space.Compact style={{ width: '100%' }}>
        <Input
          size="small"
          placeholder={placeholder}
          value={value}
          maxLength={fieldName === 'inventory_number' ? 250 : undefined}
          onChange={(e) => {
            setValue(e.target.value);
            setResolveError('');
          }}
          onPressEnter={() => void submit()}
          status={resolveError ? 'error' : undefined}
          style={{ width: '100%' }}
        />
        <Button
          size="small"
          type="primary"
          icon={<SearchOutlined />}
          aria-label={t('common:search')}
          loading={isResolving}
          onClick={handleButtonClick}
        />
      </Space.Compact>
      {resolveError && (
        <Typography.Text type="danger" role="alert">
          {resolveError}
        </Typography.Text>
      )}
    </Form>
  );
}
